// ============================================================================
// SCREEN SAVER POR INACTIVIDAD (modo pantalla de próximas clases)
// ----------------------------------------------------------------------------
// Tras `idleMs` sin interacción del staff, activa el modo pantalla (próximas
// clases del día). Cualquier toque/tecla/scroll lo cierra y reinicia el
// contador. Mientras está activo solicita Wake Lock para que la pantalla del
// dispositivo (tablet/monitor de recepción) no se apague.
//
// Portado del screensaver de la agenda dental (Dra. Jesara) — mismo patrón.
// `show()` permite activarlo manualmente (botón "Modo pantalla" en la topbar).
// ============================================================================

import { useState, useEffect, useRef, useCallback } from 'react';

// Eventos que cuentan como "actividad del usuario" (despiertan / reinician).
// pointermove para que, como un screensaver clásico, mover el cursor lo cierre.
const ACTIVITY_EVENTS: (keyof WindowEventMap)[] = [
  'pointermove',
  'pointerdown',
  'keydown',
  'touchstart',
  'wheel',
];

export interface UseIdleScreenSaverResult {
  active: boolean;
  dismiss: () => void;
  show: () => void;
}

export function useIdleScreenSaver(
  idleMs: number = 120000,
  enabled: boolean = true
): UseIdleScreenSaverResult {
  const [active, setActive] = useState(false);
  const activeRef = useRef(active);
  useEffect(() => { activeRef.current = active; }, [active]);

  // Tras un disparo manual ignoramos el primer pointermove (el mismo clic del
  // botón genera movimiento) para que no se cierre al instante.
  const justShownRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const dismiss = useCallback(() => setActive(false), []);
  const show = useCallback(() => {
    justShownRef.current = true;
    setActive(true);
    setTimeout(() => { justShownRef.current = false; }, 700);
  }, []);

  useEffect(() => {
    if (!enabled) {
      setActive(false);
      return;
    }

    const arm = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setActive(true), idleMs);
    };

    const onInteract = () => {
      // Si el screensaver está visible, una interacción lo cierra
      // (salvo el rebote del propio clic que lo abrió).
      if (activeRef.current && !justShownRef.current) setActive(false);
      // En cualquier caso, reiniciamos el contador de inactividad.
      arm();
    };

    // En segundo plano: pausa el contador (no debe dispararse oculto). Al volver
    // al frente: cierra cualquier saver y recuenta desde cero → así NUNCA aparece
    // el screensaver al abrir/reanudar la app.
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        if (timerRef.current) clearTimeout(timerRef.current);
      } else {
        setActive(false);
        arm();
      }
    };

    arm();
    ACTIVITY_EVENTS.forEach((e) =>
      window.addEventListener(e, onInteract, { passive: true })
    );
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, onInteract));
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [enabled, idleMs]);

  // ── Wake Lock mientras el screensaver está visible ────────────────────────
  useEffect(() => {
    if (!active) return;

    let released = false;
    let sentinel: any = null;
    const nav = navigator as any;
    if (!nav?.wakeLock?.request) return;

    const request = async () => {
      try {
        sentinel = await nav.wakeLock.request('screen');
      } catch {
        /* el navegador puede negarlo (sin gesto / sin foco) — no es crítico */
      }
    };
    request();

    const onVisible = () => {
      if (document.visibilityState === 'visible' && !released) request();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      released = true;
      document.removeEventListener('visibilitychange', onVisible);
      try { sentinel?.release?.(); } catch { /* noop */ }
    };
  }, [active]);

  return { active, dismiss, show };
}

export default useIdleScreenSaver;
